import { css } from 'aphrodite';
import * as React from 'react';
import { styleSheet } from './styles';

interface IProps {
	amount: number;
	goal: number;
	hideValues?: boolean;
	isSmall?: boolean;
	label?: string;
	projected?: number;
}

const ON_TRACK_RATIO = 0.9;
const CLOSE_RATIO = 0.7;
// A sliver of fill keeps an empty bar's color readable as "behind" rather than missing.
const MIN_FILL_PERCENT = 4;

/** Percent of the bar `value` fills, clamped to 0–100; a goal of zero or less fills nothing. */
export function toPercent(value: number, goal: number) {
	if (!(goal > 0) || !(value > 0)) {
		return 0;
	}
	return Math.min((value * 100) / goal, 100);
}

export const ProgressBar: React.FC<IProps> = ({
	amount = 0,
	goal = 0,
	hideValues,
	isSmall,
	label = 'Progress toward goal',
	projected = 0,
}) => {
	const goalPercentage = toPercent(amount, goal);
	const projectedPercentage = toPercent(projected, goal);
	const ratio = goalPercentage / 100;
	const fillColor =
		ratio >= ON_TRACK_RATIO ? styleSheet.fillGreen : ratio >= CLOSE_RATIO ? styleSheet.fillYellow : styleSheet.fillRed;

	return (
		<div className={css(styleSheet.container, isSmall && styleSheet.containerSmall)}>
			{!hideValues && (
				<span className={css(styleSheet.values)}>
					{amount || 0} <span className={css(styleSheet.goal)}>/ {goal || 0}</span>
				</span>
			)}
			<div
				role='progressbar'
				aria-label={label}
				aria-valuemin={0}
				aria-valuemax={goal || 0}
				aria-valuenow={amount || 0}
				className={css(styleSheet.track, isSmall && styleSheet.trackSmall)}
			>
				<div
					className={css(styleSheet.fill, isSmall && styleSheet.fillSmall, fillColor)}
					style={{ width: `${goal > 0 ? Math.max(goalPercentage, MIN_FILL_PERCENT) : 0}%` }}
				/>
				{projected > 0 && (
					<div
						className={css(styleSheet.marker)}
						style={{ left: `${projectedPercentage}%` }}
						title={`On pace for ${projected}`}
					/>
				)}
			</div>
		</div>
	);
};
